<script lang="ts" setup>
import type { ChatToolResultContent } from '#layers/ai/types/chat';
import type { UiToolContentBlock } from '#layers/ai/types/ui-chat';

import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import StructuredPreview from './StructuredPreview.vue';

const props = defineProps<UiToolContentBlock>();
const { t } = useI18n();

const showToolError = computed(() => props.output !== undefined && props.isError === true);

function previewPayloadFor(content: ChatToolResultContent) {
	if (content.type === 'json') {
		return content.data;
	}

	return content.text;
}

function previewValueFor(section: string) {
	if (section === 'input') {
		return props.input;
	}

	if (props.output === undefined) {
		return t('ai.chat.tool_output_pending');
	}

	if (props.output.length === 1 && props.output[0]) {
		return previewPayloadFor(props.output[0]);
	}

	return props.output.map((content) => previewPayloadFor(content));
}
</script>

<template>
	<DAccordion
		:items="[
			{
				label: props.intent ?? props.name ?? '',
				value: 'tool',
			},
		]"
		type="single"
		collapsible
		class="rounded-lg border border-muted bg-elevated/40"
		:ui="{
			item: 'border-none',
			trigger: 'h-8 ps-1 pe-2 text-xs',
			content: 'overflow-hidden',
			leadingIcon: 'size-4 text-muted',
			label: 'font-medium text-toned',
			body: 'pb-0',
		}"
	>
		<template v-if="showToolError" #trailing>
			<span class="flex h-8 items-center">
				<DTooltip :text="$t('ai.chat.tool_status_error')">
					<DIcon
						name="lucide:triangle-alert"
						class="size-4 shrink-0 text-muted"
						:aria-label="$t('ai.chat.tool_status_error')"
					/>
				</DTooltip>
			</span>
		</template>

		<template #content>
			<DAccordion
				:items="[{
					label: $t('ai.chat.tool_input'),
					value: 'input',
				}, {
					label: output === undefined ? $t('ai.chat.tool_output_pending') : $t('ai.chat.tool_output'),
					value: 'output',
				}]"
				type="multiple"
				:default-value="['output']"
				class="overflow-hidden rounded-md border border-muted/80 bg-default/60"
				:ui="{
					item: 'border-none',
					header: 'border-b border-muted/70 last:border-b-0',
					trigger: 'h-8 ps-1 pe-2 text-xs',
					leadingIcon: 'size-4 text-muted',
					label: 'text-xs font-medium tracking-wide text-muted',
					body: 'pb-0',
				}"
			>
				<template #body="{ item }">
					<StructuredPreview :value="previewValueFor(item.value)" />
				</template>
			</DAccordion>
		</template>
	</DAccordion>
</template>
